import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow
} from '@mui/material';
import { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { SKOLA } from 'src/constants/role';
import { useTranslation } from 'react-i18next';
import { getSchoolInfo } from 'src/api/schoolMetod';
import { RoleContext } from 'src/context/roleContext';
import Students from 'src/scenes/students';
import { API_BASE_URL } from 'src/apiConfig';
import GenderPieChart from 'src/scenes/statistics/StatResultByGender/GenderPieChart';
import { Bar } from 'react-chartjs-2';
import { ResponsiveContainer } from 'recharts';
import { tokens } from 'src/theme';
import { useTheme } from '@mui/material';

/* POCETNA STRANA ZA SKOLU */

const SchoolDashboard = () => {
  const { t } = useTranslation();
  const { role, userId, setRole, setUserId } = useContext(RoleContext);
  const [schoolLogin, setSchoolLogin] = useState(null);
  const [numberOfStudent, setNumberOfStudent] = useState('');
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [studentByClass, setStudentByClass] = useState([]);
  const [students, setStudents] = useState([]);

  const theme = useTheme();

  const colors = tokens(theme.palette.mode);

  const fetchSchoolData = useCallback(async (id) => {
    try {
      const [studentRes, genderRes, classRes, studentResList] =
        await Promise.all([
          axios.get(`${API_BASE_URL}/api/students.php?school_id=${id}`),
          axios.get(
            `${API_BASE_URL}/api/statistics.php?statistics=student_count_by_gender&schoolid1=${id}`
          ),
          axios.get(
            `${API_BASE_URL}/api/statistics.php?statistics=statisticsByClass&school_id=${id}`
          ),
          axios.get(`${API_BASE_URL}/api/students.php?get_all_school_id=${id}`)
        ]);
      setNumberOfStudent(studentRes.data);
      setGenderDistribution(genderRes.data);
      setStudentByClass(classRes.data);
      setStudents(studentResList.data);
    } catch (error) {
      console.error('Error fetching school data:', error.message);
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          setRole(decodedToken.role);
          setUserId(decodedToken.user_id);
          localStorage.setItem('userID', decodedToken.user_id);
        } catch (error) {
          console.error('Error decoding token:', error.message);
        }
      }
    };
    initialize();
  }, [setRole, setUserId]);

  useEffect(() => {
    if (role === SKOLA && userId) {
      const idMatch = userId.match(/\d+/);
      if (idMatch) {
        const schoolId = idMatch[0];
        setUserId(schoolId);
        getSchoolInfo(schoolId)
          .then((data) => setSchoolLogin(data))
          .catch((error) =>
            console.error('Error fetching school info:', error)
          );
        fetchSchoolData(schoolId);
      }
    }
  }, [role, userId, fetchSchoolData]);

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" mb="20px">
        <Typography variant="h6">
          {t('text.date')}: {new Date().toLocaleDateString()}
        </Typography>
        <Typography variant="h6">
          {t('text.studentCount')}: {numberOfStudent?.count || 0}
        </Typography>
      </Box>

      <Box mt="20px">
        {userId && role === SKOLA && (
          <Box width="100%">
            <Students
              schoolId={userId}
              dashboard={1}
              studentsList={students ? students : []}
            />
          </Box>
        )}
      </Box>

      <Grid container spacing={3} sx={{ marginTop: '20px' }}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <GenderPieChart
                data={genderDistribution}
                showTitle={1}
                colors={['#f4c2c2 ', '#a2d5f2']}
                valueKey="count"
              />
            </ResponsiveContainer>
            <Table
              sx={{ width: '80%', marginTop: '20px' }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('columns.gender')}</TableCell>
                  <TableCell align="right">
                    {t('statistics.numberOfStudents')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {genderDistribution.map((row, index) => (
                  <TableRow key={`${row.gender}-${index}`}>
                    <TableCell component="th" scope="row">
                      {row.Pol === 'm'
                        ? t('statistic.male')
                        : t('statistic.female')}
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              height: '400px'
            }}
          >
            <Typography variant="h6" align="center" color="textPrimary">
              {t('statistics.studentByClass')}
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <Bar
                data={{
                  labels: studentByClass.map(
                    (item) => `${t('statistics.class')} ${item.Razred}`
                  ),
                  datasets: [
                    {
                      label: t('statistics.numberOfStudents'),
                      data: studentByClass.map((item) => item['Broj Ucenika']),
                      backgroundColor: '#2596be',
                      borderColor: '#1b7ba6',
                      borderWidth: 1,
                      hoverBackgroundColor: '#87c4e0'
                    }
                  ]
                }}
                options={{ maintainAspectRatio: false }}
              />
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" mb="20px">
        <Typography variant="h6" color={colors.grey[600]}>
          {t('statistics.date')}: {new Date().toLocaleDateString()}
        </Typography>
        <Typography variant="h6" color={colors.grey[200]}>
          {t('statistics.studentCount')}: {numberOfStudent?.count || 0}
        </Typography>
      </Box>
    </Box>
  );
};

export default SchoolDashboard;
